import type { CommodityUnderlying } from 'models/underlying/commodity';
import type { CreditUnderlying } from 'models/underlying/credit';
import type { EquityUnderlying } from 'models/underlying/equity';
import type { ForexUnderlying } from 'models/underlying/forex';
import type { RateUnderlying } from 'models/underlying/rate';
import type { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import type { IHttpClient } from 'utils/http/httpClient.model';

export function underlyingUniverseConnector(httpClient: IHttpClient, timeoutInMillis: number = 30000) {
  return {
    fetchAll(): Observable<IUnderlyingUniverse> {
      return httpClient
        .postJSON<IUnderlyingUniverse>({
          url: 'api/v1/underlyings',
          body: { query: null },
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload),
        );
    },
    fetchCSV(asset: Asset, query?: string): Observable<CsvResponse> {
      return httpClient
        .postRAW({
          url: 'api/v1/underlyings/csv',
          body: buildExportRequest(asset, query),
        })
        .pipe(map(response => ({ attachmentName: response.attachmentName!, payload: response.payload })));
    },
  };
}

function buildExportRequest(asset: Asset, query?: string): ExportUnderlyingRequest {
  return {
    productType: 'Autocall',
    chosenAsset: asset,
    query,
  };
}

export interface IUnderlyingUniverse {
  equityUnderlyings: EquityUnderlying[];
  commodityUnderlyings?: CommodityUnderlying[];
  creditRateUnderlyings: CreditUnderlying[];
  forexUnderlyings: ForexUnderlying[];
  rateUnderlyings: RateUnderlying[];
  hasEquityAuthorization: boolean;
  hasCommodityAuthorization: boolean;
  hasCreditAuthorization: boolean;
  hasForexAuthorization: boolean;
  hasRateAuthorization: boolean;
}

export interface ExportUnderlyingRequest {
  productType: ProductType;
  chosenAsset: Asset;
  query?: string;
}

type ProductType =
  | 'Autocall'
  | 'ReverseConvertible'
  | 'CreditLinkedNote'
  | 'Participation'
  | 'ExoParticipation'
  | 'Accumulator'
  | 'FxDualCurrency';

export type Asset = 'Equity' | 'CreditRate' | 'Commodity' | 'Forex' | 'Rate';

export interface CsvResponse {
  attachmentName: string;
  payload: string;
}
